import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Award, Clock, Globe, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { products, testimonials } from '../data/content';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <div>
      {/* Hero Section with Video Background */}
      <section className="relative overflow-hidden min-h-[600px] flex items-center">
        {/* Video Background - Using working free stock videos */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="/images/industrial-poster.jpg"
        >
          <source src="https://assets.mixkit.co/videos/preview/mixkit-worker-in-a-factory-warehouse-4906-large.mp4" type="video/mp4" />
          <source src="https://assets.mixkit.co/videos/preview/mixkit-industrial-warehouse-with-boxes-4389-large.mp4" type="video/mp4" />
          <source src="https://assets.mixkit.co/videos/preview/mixkit-boxes-in-warehouse-4391-large.mp4" type="video/mp4" />
        </video>
        
        {/* 50% Black Overlay */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Content */}
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <motion.div className="max-w-4xl mx-auto text-center space-y-8 text-white" {...fadeInUp}>
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
              <Award className="h-4 w-4 text-white" />
              <span className="text-sm font-medium">Trusted by 50% of Indian FIBC Producers</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white">
              The Backbone of <span className="text-[#ed3237]">FIBC Manufacturing</span>
            </h1>
            
            <p className="text-lg text-white/90 leading-relaxed max-w-3xl mx-auto">
              Premium polypropylene yarns, dust proofing solutions, and complete FIBC essentials. 
              Engineered for quality, delivered with precision.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
              {[
                { id: 'clients', icon: Users, label: '500+ Clients', value: '500+' },
                { id: 'countries', icon: Globe, label: 'Countries Served', value: '15+' },
                { id: 'leadtime', icon: Clock, label: 'Lead Time', value: '24h' },
                { id: 'iso', icon: Award, label: 'ISO Certified', value: 'ISO' },
              ].map((stat) => (
                <div key={stat.id} className="text-center">
                  <stat.icon className="h-8 w-8 mx-auto mb-2 text-[#ed3237]" />
                  <div className="font-bold text-2xl">{stat.value}</div>
                  <div className="text-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link to="/contact" data-testid="hero-get-quote-button">
                <Button size="lg" className="w-full sm:w-auto">
                  Get a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/products">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/20 bg-white/10">
                  View Products
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-3xl mx-auto text-center space-y-6" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#ed3237]">
              Trusted Leader in <span className="text-foreground">FIBC Essentials</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Big Bag Essentials is the undisputed leader in designing, manufacturing and supplying 
              all the essentials required for jumbo bags and FIBC manufacturing. We focus on quality products 
              and we do not compromise on it, whether it is our raw materials or the machines.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
              {[
                { id: 'german-tech', text: 'German Technology' },
                { id: 'ex-stock', text: 'Ex-Stock Availability' },
                { id: 'lead-time', text: '24h Lead Times' },
                { id: 'uv-options', text: 'UV & Non-UV Options' }
              ].map((feature) => (
                <div key={feature.id} className="flex items-center justify-center space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-[#ed3237] flex-shrink-0" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-red-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4 text-[#ed3237]">
              Our Product Range
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Complete range of FIBC essentials, all available ex-stock with fast delivery
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {products.slice(0, 3).map((product, idx) => (
              <motion.div
                key={product.id}
                {...fadeInUp}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={`/products/${product.id}`}>
                  <Card className="overflow-hidden h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{product.name}</CardTitle>
                        <Badge variant="secondary">{product.category}</Badge>
                      </div>
                      <CardDescription className="line-clamp-2">
                        {product.shortDescription}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {product.specs.map((spec, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/products">
              <Button size="lg" variant="default" className="bg-primary hover:bg-primary/90 text-white">
                View All Products <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4 text-[#ed3237]">
              How We Work
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Making the highest quality yarns from scratch with cutting-edge German technology
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'PREP',
                description: 'We use the best raw materials and resins to manufacture high-quality yarns and other solutions.'
              },
              {
                step: '2',
                title: 'MANUFACTURE',
                description: 'Our team of experts uses the best German technology to create products that are both durable and flexible.'
              },
              {
                step: '3',
                title: 'DELIVERY',
                description: 'We deliver directly to your door with multiple shipping options - Full Truck Load, LCL, or Courier.'
              }
            ].map((step, idx) => (
              <motion.div
                key={idx}
                {...fadeInUp}
                transition={{ delay: idx * 0.15 }}
              >
                <Card className="relative h-full">
                  <CardHeader>
                    <div className="text-5xl font-bold text-primary/20 absolute -top-4 -left-2">
                      {step.step}
                    </div>
                    <CardTitle className="pt-8">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{step.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-red-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4 text-[#ed3237]">
              What Our Clients Say
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                {...fadeInUp}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full">
                  <CardHeader>
                    <div className="flex items-center space-x-1 mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <CardDescription className="italic">
                      "{testimonial.testimonial}"
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.company}</div>
                    <div className="text-xs text-muted-foreground">{testimonial.location}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div className="max-w-3xl mx-auto space-y-6" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold text-white">Ready to Get Started?</h2>
            <p className="text-lg opacity-90">
              Get a quote within 24 hours. Our team is ready to help you find the perfect solution for your FIBC needs.
            </p>
            <div className="pt-4">
              <Link to="/contact" data-testid="cta-get-quote-button">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                  Get a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
